/**
 * SIZE GUIDE DRAWER
 * ---------------------------------------------------------------------------
 * Behaviour for snippets/size-guide-drawer.liquid: the wrist calculator and
 * the tap-to-play video.
 *
 * Why this is its own file: the guide it replaces (size-measure-bracelet)
 * kept its script inline and found its nodes with fixed ids (#videoElement,
 * #mediaContainer) and page-wide classes (.play-icon), so a second guide or
 * any other play button on the page would have collided with it.
 *
 * Nothing here uses an id. Every lookup starts from the [data-size-guide]
 * root the event happened in, so any number of guides (bracelets today,
 * rings and necklaces later) can sit on one page without touching each other.
 * All handlers are delegated on `document`, same as drawer-core.js.
 *
 * Open/close is NOT handled here. It comes from drawer-core.js, which fires
 * drawer:open / drawer:close on the panel.
 *
 * Requires jQuery (loaded globally in layout/theme.liquid).
 */
(function () {
  'use strict';

  var ROOT = '[data-size-guide]';
  var MEDIA = '[data-sg-media]';
  var EMPTY = '—';
  // How long the pause icon stays up after playback starts.
  var PAUSE_HINT_MS = 1000;
  // On a video that is meant to be playing: the drawer is open and the
  // customer has not paused it. See the visibilitychange handler.
  var WANTED = 'data-sg-wanted';

  // ---------------- Calculator ----------------
  // Each result cell carries how much to add to the wrist size (data-sg-add),
  // so the tiers and their offsets are whatever the theme settings say.
  function updateCalculator($root, raw) {
    var wrist = parseFloat(raw);
    var unit = $root.attr('data-sg-unit') || '';

    $root.find('[data-sg-add]').each(function () {
      var $cell = $(this);
      var add = parseFloat($cell.attr('data-sg-add'));

      if (raw === '' || isNaN(wrist) || isNaN(add)) {
        $cell.text(EMPTY);
        return;
      }
      // A space before the unit, as in the size table ("16.0 cm").
      $cell.text((wrist + add).toFixed(1) + (unit ? ' ' + unit : ''));
    });
  }

  $(document).on('input', ROOT + ' [data-sg-input]', function () {
    updateCalculator($(this).closest(ROOT), $(this).val());
  });

  // ---------------- Video ----------------
  // Tap to play, with sound; tap again to pause. Nothing plays by itself,
  // unless the guide's "Muted video" setting is on: then the video carries
  // data-sg-autoplay and plays silently, on repeat, while the drawer is open.
  //
  // Tried first and dropped: autoplay (muted) on drawer open with a separate
  // mute button. A muted video needed a second control to be useful, and the
  // client preferred one obvious play button over two. The muted mode added
  // on 13 Oct 2026 is not that: it is for a video with no sound to turn on,
  // so there is still only the one control.
  //
  // What is on screen is driven by the video's own events, never by the
  // click, so it stays right when the browser refuses to play, the file
  // fails, the network stalls, or the drawer closes mid-playback:
  //
  //   paused / ended / failed ........ play icon
  //   asked to play, not started ..... spinner            (.is-loading)
  //   playing ........................ nothing            (.is-playing)
  //   first second of playing ........ pause icon         (.show-pause)
  //   stalled mid-playback ........... spinner            (.is-loading)

  function getVideo($scope) {
    return $scope.find('[data-sg-video]')[0];
  }

  function isGuideVideo(node) {
    return !!(node && node.hasAttribute && node.hasAttribute('data-sg-video'));
  }

  function isAutoplay(video) {
    return video.hasAttribute('data-sg-autoplay');
  }

  function clearPauseHint($media) {
    clearTimeout($media.data('sgHintTimer'));
    $media.removeData('sgHintTimer').removeClass('show-pause');
  }

  function showPauseHint($media) {
    clearPauseHint($media);
    $media.addClass('show-pause');
    $media.data('sgHintTimer', setTimeout(function () {
      $media.removeClass('show-pause');
    }, PAUSE_HINT_MS));
  }

  function setLabel($media, playing) {
    var $toggle = $media.find('[data-sg-video-toggle]');
    $toggle.attr('aria-label', $toggle.attr(playing ? 'data-label-pause' : 'data-label-play'));
  }

  // Back to the resting state: play icon, nothing pending.
  function showIdle($media) {
    clearPauseHint($media);
    $media.removeData('sgHintPending').removeClass('is-playing is-loading');
    setLabel($media, false);
  }

  function play(video) {
    var attempt = video.play();
    // Rejected when the browser blocks playback, or when a pause lands before
    // it started (a quick double tap). Either way the events below have
    // already put the play icon back, so there is nothing to do here.
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {});
    }
  }

  // The whole video area is the button, so "click on the video" pauses.
  $(document).on('click', ROOT + ' [data-sg-video-toggle]', function () {
    var video = getVideo($(this).closest(MEDIA));
    if (!video) return;
    if (video.paused || video.ended) {
      video.setAttribute(WANTED, 'true');
      play(video);
    } else {
      video.removeAttribute(WANTED);
      video.pause();
    }
  });

  // Media events do not bubble, so they are caught in the capture phase on
  // `document` rather than bound to each video.
  function onMediaEvent(e) {
    var video = e.target;
    // A broken file reports its error on the <source>, not the <video>.
    if (e.type === 'error' && video && video.tagName === 'SOURCE') video = video.parentNode;
    if (!isGuideVideo(video)) return;

    var $media = $(video).closest(MEDIA);

    switch (e.type) {
      case 'play':
        // Asked to play; frames may still be seconds away on a slow line.
        // No pause hint on a video that starts by itself: the customer did
        // not tap anything, so an icon flashing over it would only distract.
        $media.addClass('is-playing is-loading').data('sgHintPending', !isAutoplay(video));
        setLabel($media, true);
        break;

      case 'playing':
        $media.removeClass('is-loading');
        // Only on the start the customer asked for, not each time playback
        // resumes after buffering.
        if ($media.data('sgHintPending')) {
          $media.removeData('sgHintPending');
          showPauseHint($media);
        }
        break;

      case 'waiting':
        if (!video.paused) $media.addClass('is-loading');
        break;

      case 'error':
        // A video that failed to load still counts itself as "playing"
        // (paused stays false), so the next tap would try to pause it and
        // appear to do nothing. Pausing here makes that tap a retry.
        if (!video.paused) video.pause();
        showIdle($media);
        break;

      case 'pause':
      case 'ended':
        showIdle($media);
        break;
    }
  }

  ['play', 'playing', 'waiting', 'pause', 'ended', 'error'].forEach(function (type) {
    document.addEventListener(type, onMediaEvent, true);
  });

  // Opening the drawer shows a still, but does not play. A picked video
  // brings its own poster; a pasted file URL has none, so its first frame is
  // loaded instead (metadata only, not the whole file). Both are held back
  // until now so a closed drawer costs the page nothing.
  //
  // A muted video then starts, on every open, not only the first. If the
  // browser refuses, the play icon is still showing and a tap starts it.
  $(document).on('drawer:open', ROOT, function () {
    var video = getVideo($(this));
    if (!video) return;

    if (!video.getAttribute('data-sg-primed')) {
      video.setAttribute('data-sg-primed', 'true');

      var poster = video.getAttribute('data-sg-poster');
      if (poster) {
        video.setAttribute('poster', poster);
      } else if (!isAutoplay(video)) {
        video.preload = 'metadata';
        video.load();
      }
    }

    if (isAutoplay(video)) {
      // Browsers only allow unprompted playback of a muted video, and go by
      // this property, not the attribute.
      video.muted = true;
      video.setAttribute(WANTED, 'true');
      play(video);
    }
  });

  // Closing by any route (X, outside click, Escape, another drawer opening)
  // stops the sound. The position is kept, so reopening resumes from there.
  $(document).on('drawer:close', ROOT, function () {
    var video = getVideo($(this));
    if (!video) return;
    video.removeAttribute(WANTED);
    if (!video.paused) video.pause();
  });

  // Leaving the tab stops the sound too. A muted video starts again on
  // coming back: on a phone, a glance at another app would otherwise leave
  // it paused under a play icon the customer never had to tap in the first
  // place. One the customer paused, or whose drawer is closed, stays paused.
  //
  // This goes by WANTED rather than by "was it playing when the tab was
  // hidden", because the browser may pause a muted video in a background
  // tab itself, before this handler runs.
  document.addEventListener('visibilitychange', function () {
    $(ROOT + ' [data-sg-video]').each(function () {
      if (document.hidden) {
        if (!this.paused) this.pause();
      } else if (isAutoplay(this) && this.getAttribute(WANTED) && this.paused) {
        play(this);
      }
    });
  });

  // ---------------- Chat link ----------------
  // The global .open-chat handler (assets/custom.js.liquid) is a delegated
  // document listener, so it still fires on this same click. Deliberately no
  // preventDefault/stopPropagation: this only closes the drawer so the chat
  // window is not left underneath its overlay.
  $(document).on('click', ROOT + ' .open-chat', function () {
    if (window.DrawerCore) window.DrawerCore.closeAll();
  });
})();
